import {
  EMPLOYMENT_TYPES,
  EMPLOYMENT_TYPE_LABELS,
  SENIORITY_LEVELS,
  WORK_MODELS,
  WORK_MODEL_LABELS,
  jobCreateSchema,
  type EmploymentType,
  type JobCreateInput,
  type JobCriterion,
  type StandortOrigin,
  type WorkModel,
} from "@vidario/domain";
import { Button, Card, cn, Input, Select, Textarea, useToast } from "@vidario/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { CalendarDays, Plus, RefreshCw, Send, Sparkles, X } from "lucide-react";
import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type FocusEvent,
  type ChangeEvent,
  type ReactElement,
  type ReactNode,
} from "react";
import { Controller, useForm, useWatch } from "react-hook-form";

import { ImportCard, ParserChecklist, type ImportResult } from "./ImportCard";
import { StandortField } from "./StandortField";

// The Select can't hold an empty value, so "no work model" has its own.
const NO_WORK_MODEL = "none";

export const EMPTY_JOB: JobCreateInput = {
  title: "",
  company: "",
  location: "",
  workModel: null,
  lat: null,
  lng: null,
  industry: "",
  description: "",
  tasks: [],
  benefits: [],
  hiringPriorities: [],
  criteria: [],
  seniority: null,
  employmentType: null,
  salaryFrom: null,
  salaryTo: null,
  targetStartDate: null,
  notes: null,
  sourceUrl: null,
};

export interface JobFormProps {
  /**
   * - `create`: shows the auto-import card and both submit paths
   *   (Entwurf / An Vidario senden).
   * - `edit-draft`: same submit paths as create, no import card.
   * - `edit-running`: single "Änderungen speichern" button — the
   *   stelle is already published, so the publish-action is gone.
   */
  mode: "create" | "edit-draft" | "edit-running";
  initialValues?: JobCreateInput;
  submitting?: boolean;
  onCancel: () => void;
  /** Called with (values, submit). `submit=false` keeps the job in
   * draft / saves changes to a running stelle; `submit=true` sends
   * the job to Vidario (saves it, then starts its search). The parent decides
   * whether this is a create or update. */
  onSubmit: (values: JobCreateInput, submit: boolean) => void;
  /** Admin-only, `edit-running` only: save the edited parameters and
   * immediately restart the candidate search with them. Receives the
   * validated values; the parent handles the confirm + restart. When
   * omitted the extra button is hidden. */
  onSaveAndRestart?: (values: JobCreateInput) => void;
  /** The backend's reasons per field from a failed save (#22). */
  serverErrors?: Record<string, string[]>;
  /** Edit: the saved job already has coordinates for its Standort (#49). */
  placeConfirmed?: boolean;
}

/** Shared create/edit form. Create flow shows the auto-import card and
 * renders a fresh shell; edit flow skips the import (the job already
 * exists) and can be prefilled via `initialValues`. Exposes two submit
 * paths — „Entwurf speichern" keeps the job in `draft`, „An Vidario
 * senden" transitions it out of draft and locks it. */
export function JobForm({
  mode,
  initialValues,
  submitting = false,
  onCancel,
  onSubmit,
  onSaveAndRestart,
  serverErrors,
  placeConfirmed = false,
}: JobFormProps): ReactElement {
  const toast = useToast();
  const [justImported, setJustImported] = useState(false);
  // A picked place, or a saved one that already has coordinates (#49).
  const [standortConfirmed, setStandortConfirmed] = useState(placeConfirmed);
  const [standortOrigin, setStandortOrigin] = useState<StandortOrigin>(
    mode !== "create" && initialValues?.location && !placeConfirmed ? "saved" : "typed",
  );

  const {
    register,
    handleSubmit,
    control,
    reset,
    setError,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm<JobCreateInput>({
    resolver: zodResolver(jobCreateSchema),
    defaultValues: initialValues ?? EMPTY_JOB,
  });

  const workModel = useWatch({ control, name: "workModel" });

  // Only the latest failure's reasons: clear the fields the previous one
  // marked before marking the new ones (review on #94).
  const serverFields = useRef<(keyof JobCreateInput)[]>([]);
  useEffect(() => {
    clearErrors(serverFields.current);
    const fields = Object.keys(serverErrors ?? {}).filter(
      (field): field is keyof JobCreateInput => field in EMPTY_JOB,
    );
    for (const field of fields) {
      setError(field, { type: "server", message: (serverErrors?.[field] ?? []).join(" ") });
    }
    serverFields.current = fields;
  }, [serverErrors, setError, clearErrors]);

  const handleImported = (result: ImportResult): void => {
    reset({ ...EMPTY_JOB, ...result.values }, { keepErrors: false });
    // An imported place is a suggestion to confirm, never confirmed by itself.
    setStandortConfirmed(false);
    setStandortOrigin("imported");
    setJustImported(true);
    toast.push({
      kind: "success",
      title: "Import abgeschlossen",
      message: "Bitte Felder prüfen und ergänzen, bevor du speicherst.",
    });
  };

  const handleImportError = (message: string): void => {
    // Nothing was imported: say so, with the parser's reason (#22).
    toast.push({ kind: "warning", title: "Import fehlgeschlagen", message });
  };

  // Hide the auto-import card once the form has been populated from a
  // successful import so we don't overwrite the recruiter's manual edits.
  // Only the create flow ever shows it.
  const showImportCard = mode === "create" && !justImported;
  // The "An Vidario senden" form-level submit only makes sense when a
  // publish path exists. In edit-running there is no publish — we
  // intercept the implicit Enter-key submit instead.
  const canPublish = mode === "create" || mode === "edit-draft";

  const submitWith = (shouldPublish: boolean) =>
    handleSubmit((values) => onSubmit(values, shouldPublish));

  return (
    <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-[1fr_320px]">
      <form
        onSubmit={(e) => void submitWith(canPublish)(e)}
        noValidate
        className="flex min-w-0 flex-col gap-6"
        data-testid="job-create-form"
      >
        {showImportCard && (
          <ImportCard onImported={handleImported} onImportError={handleImportError} />
        )}
        {mode === "create" && justImported && (
          <Card className="border-primary-100 bg-primary-50">
            <div className="flex items-start gap-2.5 text-[13px] text-primary-800">
              <Sparkles size={16} className="mt-0.5 shrink-0 text-primary-600" />
              <div>
                <b>Import übernommen.</b> Bitte prüfe die Felder und speichere, wenn alles passt. Um
                erneut zu importieren, breche ab und starte neu.
              </div>
            </div>
          </Card>
        )}

        <Section title="Eckdaten">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="Jobtitel"
              required
              placeholder="z. B. Senior Data Engineer"
              {...register("title")}
              {...(errors.title?.message && { error: errors.title.message })}
            />
            <Input
              label="Unternehmen"
              required
              {...register("company")}
              {...(errors.company?.message && { error: errors.company.message })}
            />
            <Controller
              control={control}
              name="location"
              render={({ field }) => (
                <StandortField
                  value={field.value}
                  onType={(text) => {
                    field.onChange(text);
                    setValue("lat", null);
                    setValue("lng", null);
                    setStandortConfirmed(false);
                    setStandortOrigin("typed");
                  }}
                  onPick={(place) => {
                    field.onChange(place.label);
                    setValue("lat", place.lat);
                    setValue("lng", place.lng);
                    setStandortConfirmed(true);
                  }}
                  confirmed={standortConfirmed}
                  origin={standortOrigin}
                  workModel={workModel}
                  error={errors.location?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="workModel"
              render={({ field }) => (
                <Select
                  label="Arbeitsmodell"
                  placeholder="Bitte wählen"
                  value={field.value ?? ""}
                  onValueChange={(v) =>
                    field.onChange(v === "" || v === NO_WORK_MODEL ? null : (v as WorkModel))
                  }
                  // Optional, so a choice can be taken back.
                  options={[
                    { value: NO_WORK_MODEL, label: "Nicht angegeben" },
                    ...WORK_MODELS.map((m) => ({ value: m, label: WORK_MODEL_LABELS[m] })),
                  ]}
                />
              )}
            />
            <Input
              label="Branche"
              placeholder="z. B. Automotive, FinTech, Maschinenbau"
              {...register("industry")}
              {...(errors.industry?.message && { error: errors.industry.message })}
            />
          </div>
        </Section>

        <Section title="Beschreibung">
          <div className="flex flex-col gap-4">
            <Textarea
              label="Kurzbeschreibung"
              rows={3}
              placeholder="1–3 Sätze: Was tut diese Person konkret, in welchem Team-/Produkt-Kontext?"
              {...register("description")}
              {...(errors.description?.message && { error: errors.description.message })}
            />
            <Controller
              control={control}
              name="tasks"
              render={({ field }) => (
                <BulletTextarea
                  label="Aufgaben"
                  rows={5}
                  placeholder="Eine Aufgabe pro Zeile."
                  values={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <Controller
              control={control}
              name="benefits"
              render={({ field }) => (
                <BulletTextarea
                  label="Wir bieten"
                  rows={4}
                  placeholder="Ein Benefit pro Zeile."
                  values={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </div>
        </Section>

        <Section title="Anforderungen">
          <p className="mb-3.5 text-xs text-text-tertiary">
            Eine Liste pro Stelle. Knock-out heißt: Bewerber ohne dieses Kriterium fallen raus.
            Wichtigkeit (1–10) gewichtet den Score — wichtige Kriterien zählen mehr. Beschreibung
            ist optional und wird beim Scoring zusätzlich gelesen.
          </p>
          <Controller
            control={control}
            name="criteria"
            render={({ field }) => (
              <CriteriaEditor values={field.value} onChange={field.onChange} />
            )}
          />
          <div className="mt-4 max-w-72">
            <Controller
              control={control}
              name="seniority"
              render={({ field }) => (
                <Select
                  label="Seniority"
                  placeholder="Bitte wählen"
                  value={field.value ?? ""}
                  onValueChange={(v) =>
                    field.onChange(v === "" ? null : (v as (typeof SENIORITY_LEVELS)[number]))
                  }
                  options={SENIORITY_LEVELS.map((s) => ({ value: s, label: s }))}
                />
              )}
            />
          </div>
        </Section>

        <Section title="Rahmenbedingungen">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Controller
              control={control}
              name="employmentType"
              render={({ field }) => (
                <Select
                  label="Beschäftigungsart"
                  placeholder="Bitte wählen"
                  value={field.value ?? ""}
                  onValueChange={(v) => field.onChange(v === "" ? null : (v as EmploymentType))}
                  options={EMPLOYMENT_TYPES.map((t) => ({
                    value: t,
                    label: EMPLOYMENT_TYPE_LABELS[t],
                  }))}
                />
              )}
            />
            <Input
              label="Avisiertes Einstellungsdatum"
              type="date"
              icon={<CalendarDays size={16} />}
              {...register("targetStartDate", {
                setValueAs: (v: unknown) => {
                  const s = typeof v === "string" ? v.trim() : "";
                  return s === "" ? null : s;
                },
              })}
              {...(errors.targetStartDate?.message && {
                error: errors.targetStartDate.message,
              })}
            />
          </div>
        </Section>

        <Section title="Konditionen">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="Gehalt von (EUR/Jahr)"
              type="number"
              placeholder="80000"
              {...register("salaryFrom", {
                setValueAs: (v) => (v === "" || v === null ? null : Number(v)),
              })}
              {...(errors.salaryFrom?.message && { error: errors.salaryFrom.message })}
            />
            <Input
              label="Gehalt bis (EUR/Jahr)"
              type="number"
              placeholder="110000"
              {...register("salaryTo", {
                setValueAs: (v) => (v === "" || v === null ? null : Number(v)),
              })}
              {...(errors.salaryTo?.message && { error: errors.salaryTo.message })}
            />
          </div>
        </Section>

        <Section title="Einstellungsprioritäten (optional)">
          <Controller
            control={control}
            name="hiringPriorities"
            render={({ field }) => (
              <BulletTextarea
                label="Worauf kommt es an?"
                rows={4}
                placeholder={
                  "z. B. „Sofortiger Start gewünscht“, " +
                  "„Office-Präsenz 3×/Woche“, " +
                  "„Cultural Fit vor technischer Perfektion“"
                }
                values={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </Section>

        <Section title="Briefing (optional)">
          <Textarea
            label="Anmerkungen"
            rows={4}
            placeholder="Hinweise zum Team, Sprache, Relocation, Sonstiges…"
            {...register("notes", {
              setValueAs: (v: unknown) => {
                const s = typeof v === "string" ? v : "";
                return s === "" ? null : s;
              },
            })}
          />
        </Section>

        <input
          type="hidden"
          {...register("sourceUrl", {
            setValueAs: (v: unknown) => {
              const s = typeof v === "string" ? v : "";
              return s === "" ? null : s;
            },
          })}
        />

        <div className="flex flex-wrap justify-end gap-3 border-t border-border-default pt-4">
          <Button type="button" variant="secondary" onClick={onCancel}>
            Abbrechen
          </Button>
          {mode === "edit-running" ? (
            <>
              {onSaveAndRestart && (
                <Button
                  type="button"
                  variant="secondary"
                  loading={submitting}
                  leftIcon={<RefreshCw size={14} />}
                  onClick={(e) => void handleSubmit((values) => onSaveAndRestart(values))(e)}
                  data-testid="job-save-and-restart"
                >
                  Speichern &amp; Suche neu starten
                </Button>
              )}
              <Button
                type="button"
                loading={submitting}
                onClick={(e) => void submitWith(false)(e)}
                data-testid="job-save-changes"
              >
                Änderungen speichern
              </Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                variant="secondary"
                loading={submitting}
                onClick={(e) => void submitWith(false)(e)}
                data-testid="job-save-draft"
              >
                Entwurf speichern
              </Button>
              <Button
                type="submit"
                loading={submitting}
                leftIcon={<Send size={14} />}
                data-testid="job-submit"
              >
                An Vidario senden
              </Button>
            </>
          )}
        </div>
      </form>

      <aside className="lg:sticky lg:top-[calc(5.5rem+var(--anonym-bar-offset,0px))]">
        <Card className="border-primary-100 bg-primary-50">
          <div className="mb-3 flex items-center gap-2">
            <Sparkles size={16} className="text-primary-600" />
            <h4 className="m-0 text-sm font-semibold text-primary-800">So funktioniert&apos;s</h4>
          </div>
          <p className="mb-3.5 mt-0 text-[13px] leading-relaxed text-primary-800">
            Der Vidario-Parser liest deine Stellenausschreibung und erkennt automatisch:
          </p>
          <ParserChecklist />
          <div className="mt-4 rounded-md bg-neutral-0 p-3 text-xs leading-relaxed text-text-secondary">
            <b>Tipp:</b> Je strukturierter die Quelle, desto vollständiger der Import.
            Nachträgliches Editieren ist nur solange möglich, wie die Stelle noch ein Entwurf ist.
          </div>
        </Card>

        <Card className="mt-4">
          <h4 className="m-0 mb-2.5 text-[13px] font-semibold uppercase tracking-wider text-text-secondary">
            Nach dem Senden
          </h4>
          <ol className="m-0 flex list-decimal flex-col gap-1 pl-4.5 text-[13px] leading-relaxed text-text-secondary">
            <li>Vidario-Team erhält Benachrichtigung</li>
            <li>Longlist wird in 24–48 h erstellt</li>
            <li>Du erhältst eine E-Mail zur Sichtung</li>
          </ol>
        </Card>
      </aside>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }): ReactElement {
  return (
    <section>
      <h3 className="m-0 mb-3.5 text-[13px] font-semibold uppercase tracking-wider text-text-secondary">
        {title}
      </h3>
      <div className="rounded-xl border border-border-default bg-neutral-0 p-5 shadow-sm">
        {children}
      </div>
    </section>
  );
}

/** Local textarea that round-trips `string[]` ↔ newline-joined text and
 * keeps a visible "– " prefix on every line. On Enter we auto-insert the
 * next bullet; on first focus into an empty field we seed one. Strips
 * the prefix before committing to the form payload, so the stored array
 * stays clean. Used for Aufgaben + Wir bieten + Einstellungsprioritäten. */
const BULLET = "– ";

function valuesToDisplay(values: readonly string[]): string {
  return values.length === 0 ? "" : values.map((v) => `${BULLET}${v}`).join("\n");
}

function displayToArray(raw: string): string[] {
  return raw
    .split("\n")
    .map((line) => line.replace(/^[–\-•*]\s*/, "").trim())
    .filter((line) => line.length > 0);
}

function BulletTextarea({
  label,
  rows,
  placeholder,
  values,
  onChange,
}: {
  label: string;
  rows?: number;
  placeholder?: string;
  values: readonly string[];
  onChange: (next: string[]) => void;
}): ReactElement {
  const [text, setText] = useState(() => valuesToDisplay(values));
  // Remember the array we last produced so we only re-sync when the parent
  // hands us a genuinely new one (import, edit-prefill). Without this the
  // cursor would jump on every keystroke because RHF round-trips the state.
  const lastOutboundRef = useRef<readonly string[]>(values);

  useEffect(() => {
    if (values !== lastOutboundRef.current) {
      setText(valuesToDisplay(values));
      lastOutboundRef.current = values;
    }
  }, [values]);

  const commit = (next: string): void => {
    setText(next);
    const arr = displayToArray(next);
    lastOutboundRef.current = arr;
    onChange(arr);
  };

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    commit(e.target.value);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key !== "Enter" || e.shiftKey) return;
    e.preventDefault();
    const target = e.currentTarget;
    const start = target.selectionStart;
    const end = target.selectionEnd;
    const insert = `\n${BULLET}`;
    const next = text.slice(0, start) + insert + text.slice(end);
    commit(next);
    requestAnimationFrame(() => {
      const pos = start + insert.length;
      target.selectionStart = target.selectionEnd = pos;
    });
  };

  const handleFocus = (e: FocusEvent<HTMLTextAreaElement>): void => {
    if (text !== "") return;
    commit(BULLET);
    const target = e.currentTarget;
    requestAnimationFrame(() => {
      target.selectionStart = target.selectionEnd = BULLET.length;
    });
  };

  return (
    <Textarea
      label={label}
      rows={rows ?? 4}
      {...(placeholder !== undefined && { placeholder })}
      value={text}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onFocus={handleFocus}
    />
  );
}

/** Inline editor for the unified `criteria` list — Option D's single
 * "Anforderungen" table. Each row: knock-out toggle + name + optional
 * description + importance slider (1–10) + remove button. */
function CriteriaEditor({
  values,
  onChange,
}: {
  values: readonly JobCriterion[];
  onChange: (next: JobCriterion[]) => void;
}): ReactElement {
  const update = (idx: number, patch: Partial<JobCriterion>): void => {
    onChange(values.map((v, i) => (i === idx ? { ...v, ...patch } : v)));
  };
  const remove = (idx: number): void => {
    onChange(values.filter((_, i) => i !== idx));
  };
  const add = (): void => {
    onChange([...values, { name: "", importance: 5, required: false, description: "" }]);
  };

  return (
    <div className="flex flex-col gap-2.5">
      {values.length === 0 ? (
        <p className="text-[13px] italic text-text-tertiary">
          Noch keine Anforderungen. Füge Skills, Voraussetzungen und Kontext-Faktoren in einer Liste
          hinzu.
        </p>
      ) : (
        <div
          className="hidden grid-cols-[88px_1fr_220px_36px] items-center gap-3 px-3 text-[11px] sm:grid font-semibold uppercase tracking-wider text-text-tertiary"
          aria-hidden="true"
        >
          <span>K.-o.</span>
          <span>Anforderung</span>
          <span>Wichtigkeit</span>
          <span />
        </div>
      )}
      {values.map((c, idx) => (
        <div
          key={idx}
          className="grid grid-cols-[auto_minmax(0,1fr)_36px] items-start gap-3 rounded-lg sm:grid-cols-[88px_1fr_220px_36px] border border-border-default bg-neutral-50 p-3"
        >
          <label
            className="mt-2 flex cursor-pointer items-center gap-1.5 select-none"
            title="Knock-out: Bewerber ohne dieses Kriterium fallen aus dem Match raus."
          >
            <input
              type="checkbox"
              checked={c.required}
              onChange={(e) => update(idx, { required: e.target.checked })}
              className="h-4 w-4 cursor-pointer accent-danger-500"
              aria-label={c.required ? "Knock-out aktiv (deaktivieren)" : "Als Knock-out markieren"}
            />
            <span
              className={cn(
                "text-[12px] font-medium",
                c.required ? "text-danger-700" : "text-text-tertiary",
              )}
            >
              Pflicht
            </span>
          </label>
          <div className="col-span-2 flex flex-col gap-1.5 sm:col-span-1">
            <Input
              placeholder="z. B. KFZ-Mechatronik, Führerschein Klasse CE, Office-Präsenz"
              value={c.name}
              onChange={(e) => update(idx, { name: e.target.value })}
            />
            <Input
              placeholder="Beschreibung (optional) — z. B. „3 Tage/Woche in Giebelstadt"
              value={c.description}
              onChange={(e) => update(idx, { description: e.target.value })}
            />
          </div>
          {/* On a phone the slider and the remove button share a second line, in DOM order (#124). */}
          <div className="col-span-2 flex flex-col gap-1 pt-1 sm:col-span-1">
            <label className="text-xs font-medium text-text-secondary">
              Wichtigkeit · {c.importance}/10
            </label>
            <input
              type="range"
              min={1}
              max={10}
              step={1}
              value={c.importance}
              onChange={(e) => update(idx, { importance: Number(e.target.value) })}
              className="mt-1 w-full accent-primary-500"
              aria-label={`Wichtigkeit für ${c.name || "neue Anforderung"}`}
            />
          </div>
          <button
            type="button"
            onClick={() => remove(idx)}
            className="mt-1 self-start rounded-md p-2 text-text-tertiary transition-colors hover:bg-danger-50 hover:text-danger-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger-500"
            aria-label="Anforderung entfernen"
          >
            <X size={14} />
          </button>
        </div>
      ))}
      <div className="self-start">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          leftIcon={<Plus size={14} />}
          onClick={add}
        >
          Anforderung hinzufügen
        </Button>
      </div>
    </div>
  );
}
